import React, { useState, useEffect, useContext } from 'react';
import { getAvatarUrl, getStorageAvatarUrl } from '../utils/avatarUtils';
import { User, Settings as SettingsIcon, Save, CheckCircle, Image as ImageIcon, Share2, Copy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { UserContext } from '../store/useStore';
import { SafeImage } from '../components/common/SafeImage';
import Button from '../components/common/Button';
import { db } from '../lib/firebase';
import { ref, set, get } from 'firebase/database';
import { updateProfile } from 'firebase/auth';
import { AchievementService } from '../services/achievementService';
import { getUserProfileStats } from '../services/socialService';
import { Clock, Gamepad2, Brain, BookOpen } from 'lucide-react';
import ShareProfileModal from '../components/social/ShareProfileModal';

// Define some preset seeds for the DiceBear API
const PRESET_AVATARS = [
  'Felix', 'Aneka', 'Oliver', 'Abby', 'Jack', 'Mia', 
  'Jude', 'Chloe', 'Leo', 'Zoe', 'Max', 'Luna'
];

const ProfilePage: React.FC = () => {
  const { currentUser } = useAuth();
  const { userProgress, checkAchievements, userAchievements } = useContext(UserContext);
  
  const [username, setUsername] = useState('');
  const [selectedAvatarUrl, setSelectedAvatarUrl] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [profileStats, setProfileStats] = useState<any>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  
  useEffect(() => {
    if (currentUser) {
      setUsername(currentUser.displayName || currentUser.email?.split('@')[0] || '');
      setSelectedAvatarUrl(currentUser.photoURL || getStorageAvatarUrl(currentUser.uid));
      checkAchievements();
      
      getUserProfileStats(currentUser.uid)
        .then(data => setProfileStats(data))
        .catch(console.error);
    }
  }, [currentUser, checkAchievements]);

  if (!currentUser) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p>Please log in to access your profile.</p>
      </div>
    );
  }

  const handleShareProfile = () => {
    setIsShareModalOpen(true);
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      // 1. Update Firebase Auth Profile
      await updateProfile(currentUser, {
        displayName: username,
        photoURL: selectedAvatarUrl
      });

      // 2. Update Realtime DB: users node
      const userRef = ref(db, `users/${currentUser.uid}`);
      const userSnapshot = await get(userRef);
      if (userSnapshot.exists()) {
        await set(userRef, {
          ...userSnapshot.val(),
          username: username,
          photoURL: selectedAvatarUrl
        });
      }

      // 3. Update Realtime DB: ranking nodes
      const updateRankingName = async (path: string) => {
        const rankingRef = ref(db, `${path}/${currentUser.uid}`);
        const rankingSnapshot = await get(rankingRef);
        if (rankingSnapshot.exists()) {
          await set(rankingRef, {
            ...rankingSnapshot.val(),
            username: username,
            photoURL: selectedAvatarUrl
          });
        }
      };

      await updateRankingName('weekly');
      await updateRankingName('monthly');
      await updateRankingName('allTime');

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to update profile', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 mb-20 md:mb-0">
      <div className="max-w-3xl mx-auto space-y-8">
        
        {/* Top Animated CTA Share Button */}
        <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 p-[2px] rounded-3xl shadow-xl transform transition-transform hover:scale-[1.02]">
          <div className="bg-white rounded-[22px] p-6 md:p-8 text-center relative overflow-hidden">
            {/* Background floating emojis */}
            <div className="absolute top-4 left-6 text-3xl animate-bounce" style={{ animationDuration: '3s' }}>🫂</div>
            <div className="absolute bottom-4 right-8 text-3xl animate-bounce" style={{ animationDelay: '1s', animationDuration: '4s' }}>🧑‍🤝‍🧑</div>
            <div className="absolute top-1/2 right-4 text-2xl animate-pulse">🎉</div>
            <div className="absolute bottom-6 left-10 text-2xl animate-pulse" style={{ animationDelay: '0.5s' }}>👯‍♀️</div>
            
            <h2 className="text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600 mb-3 relative z-10">
              Learn English Together! 🚀
            </h2>
            <p className="text-gray-600 mb-6 text-lg relative z-10 max-w-lg mx-auto">
              Share your profile, add friends, and challenge them to practice! Learning is always better with a buddy 🫂.
            </p>
            <button
              onClick={handleShareProfile}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg animate-pulse hover:animate-none text-lg relative z-10"
            >
              <Share2 className="h-6 w-6" />
              Share My Profile
            </button>
          </div>
        </div>

        <div className="flex items-center">
          <User className="h-6 w-6 text-gray-400 mr-2" />
          <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
        </div>
        
        {saveSuccess && (
          <div className="p-4 bg-green-50 border border-green-100 rounded-lg flex items-center text-green-700">
            <CheckCircle className="h-5 w-5 mr-2" />
            Profile saved successfully!
          </div>
        )}
        
        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-800">Account Details</h2>
          </div>
          
          <div className="p-6">
            <div className="mb-6">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                {/* Avatar Selection */}
                <div className="flex-shrink-0 w-full md:w-1/3">
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <ImageIcon className="w-4 h-4 mr-1" /> Profile Picture
                  </label>
                  
                  <div className="mb-4 flex justify-center">
                    <SafeImage 
                      src={getAvatarUrl(selectedAvatarUrl)} 
                      fallbackSeed={currentUser.uid}
                      alt="Current Avatar" 
                      className="w-32 h-32 rounded-full border-4 border-indigo-100 shadow-sm"
                    />
                  </div>
                  
                  <p className="text-xs text-gray-500 mb-3 text-center">Choose an avatar</p>
                  <div className="grid grid-cols-4 gap-2 h-48 overflow-y-auto p-2 border border-gray-100 rounded-lg bg-gray-50">
                    {(() => {
                      const googleProviderData = currentUser.providerData?.find(p => p.providerId === 'google.com') || currentUser.providerData?.[0];
                      const googlePhotoUrl = googleProviderData?.photoURL;
                      
                      return googlePhotoUrl ? (
                        <div 
                          onClick={() => setSelectedAvatarUrl(googlePhotoUrl)}
                          className={`cursor-pointer rounded-full overflow-hidden border-2 transition-all ${
                            selectedAvatarUrl === googlePhotoUrl ? 'border-indigo-600 scale-110 shadow-md' : 'border-transparent hover:border-indigo-300 hover:scale-105'
                          }`}
                          title="Google Profile Photo"
                        >
                          <img referrerPolicy="no-referrer" src={googlePhotoUrl} alt="Google Avatar" className="w-full h-full object-cover" crossOrigin="anonymous" />
                        </div>
                      ) : null;
                    })()}
                    {PRESET_AVATARS.map((seed) => {
                      const storageUrl = getStorageAvatarUrl(seed);
                      const displayUrl = getAvatarUrl(seed);
                      return (
                        <div 
                          key={seed}
                          onClick={() => setSelectedAvatarUrl(storageUrl)}
                          className={`cursor-pointer rounded-full overflow-hidden border-2 transition-all ${
                            selectedAvatarUrl === storageUrl ? 'border-indigo-600 scale-110 shadow-md' : 'border-transparent hover:border-indigo-300 hover:scale-105'
                          }`}
                        >
                          <img referrerPolicy="no-referrer" src={displayUrl} alt={`Avatar ${seed}`} className="w-full h-full" />
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Form Fields */}
                <div className="w-full md:w-2/3 space-y-4">
                  <div>
                    <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                      Username
                    </label>
                    <input
                      type="text"
                      id="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Your display name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={currentUser.email || ''}
                      disabled
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Email cannot be changed.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end mb-8">
          <Button 
            onClick={handleSaveSettings}
            disabled={isSaving}
            leftIcon={<Save className="h-4 w-4" />}
          >
            {isSaving ? 'Saving...' : 'Save Profile Details'}
          </Button>
        </div>

        {/* Moved Inactivity Tracker to Your Progress section */}
        {/* Stats Grid */}
        {profileStats && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 px-2">Learning Stats</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow group">
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <Clock className="w-6 h-6" />
                </div>
                <p className="text-sm font-medium text-gray-500 mb-1">Days Studied</p>
                <p className="text-2xl font-bold text-gray-900">{profileStats.stats.daysStudied}</p>
              </div>

              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow group">
                <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <Gamepad2 className="w-6 h-6" />
                </div>
                <p className="text-sm font-medium text-gray-500 mb-1">Games Solved</p>
                <p className="text-2xl font-bold text-gray-900">{profileStats.stats.crimeGamesSolved}</p>
              </div>

              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow group">
                <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <Brain className="w-6 h-6" />
                </div>
                <p className="text-sm font-medium text-gray-500 mb-1">Daily Training</p>
                <p className="text-2xl font-bold text-gray-900">{profileStats.stats.dailyTrainingDone}</p>
              </div>

              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow group">
                <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <BookOpen className="w-6 h-6" />
                </div>
                <p className="text-sm font-medium text-gray-500 mb-1">Word of the Day</p>
                <p className="text-2xl font-bold text-gray-900">{profileStats.stats.wordOfDayDone}</p>
              </div>

            </div>
          </div>
        )}

        {/* Achievements Section */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 mb-8">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">Your Progress</h2>
            <span className="bg-indigo-100 text-indigo-800 text-sm font-medium px-3 py-1 rounded-full">
               Level {userAchievements.totalPoints > 1000 ? 'Expert' : userAchievements.totalPoints > 500 ? 'Advanced' : userAchievements.totalPoints > 100 ? 'Intermediate' : 'Beginner'}
            </span>
          </div>
          
          <div className="p-6">
            
            {/* Inactivity / Streak Animated Card */}
            {profileStats && (
              <div className="relative overflow-hidden mb-6">
                {profileStats.daysWithoutStudy > 1 || (profileStats.daysWithoutStudy === 1 && (!userProgress.streak || userProgress.streak === 0)) ? (
                  <div className="bg-gradient-to-br from-amber-50 to-orange-100 border border-orange-200 rounded-3xl p-6 shadow-md flex items-center justify-between group transform transition-transform hover:scale-[1.01]">
                    <div>
                      <h3 className="text-xl font-bold text-orange-900 mb-1">Time to study!</h3>
                      <p className="text-orange-700 font-medium">
                        You are {profileStats.daysWithoutStudy} day{profileStats.daysWithoutStudy !== 1 ? 's' : ''} without studying any activity. Let's practice!
                      </p>
                    </div>
                    <div className="text-5xl animate-bounce">😴</div>
                  </div>
                ) : profileStats.daysWithoutStudy === -1 ? (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-100 border border-indigo-200 rounded-3xl p-6 shadow-md flex items-center justify-between group transform transition-transform hover:scale-[1.01]">
                    <div>
                      <h3 className="text-xl font-bold text-indigo-900 mb-1">Welcome!</h3>
                      <p className="text-indigo-700 font-medium">
                        You haven't started studying yet. Let's get started!
                      </p>
                    </div>
                    <div className="text-5xl animate-bounce">👋</div>
                  </div>
                ) : (
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-100 border border-teal-200 rounded-3xl p-6 shadow-md flex items-center justify-between group transform transition-transform hover:scale-[1.01]">
                    <div>
                      <h3 className="text-xl font-bold text-teal-900 mb-1">On Fire! 🔥</h3>
                      <p className="text-teal-700 font-medium">
                        You have an active streak of {userProgress.streak || 1} day{(userProgress.streak || 1) !== 1 ? 's' : ''} in a row! Keep it up!
                      </p>
                    </div>
                    <div className="text-5xl animate-pulse">🤩</div>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-indigo-50 rounded-lg p-4 text-center">
                <p className="text-sm text-indigo-600 font-medium mb-1">Words Learned</p>
                <p className="text-2xl font-bold text-indigo-900">{userProgress.wordsLearned || 0}</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <p className="text-sm text-orange-600 font-medium mb-1">Day Streak</p>
                <p className="text-2xl font-bold text-orange-900">{userProgress.streak || 0} 🔥</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 text-center">
                <p className="text-sm text-yellow-600 font-medium mb-1">Total Points</p>
                <p className="text-2xl font-bold text-yellow-900">{userAchievements.totalPoints || 0}</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <p className="text-sm text-purple-600 font-medium mb-1">Achievements</p>
                <p className="text-2xl font-bold text-purple-900">{userAchievements.unlockedCount || 0}</p>
              </div>
            </div>
            
            <div className="mt-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Recent Achievements</h3>
              
              {!profileStats ? (
                <div className="space-y-3 animate-pulse">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex items-center p-3 border border-gray-100 rounded-lg bg-gray-50 h-20">
                      <div className="h-10 w-10 bg-gray-200 rounded-full mr-4"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : userAchievements.achievements && userAchievements.achievements.length > 0 ? (
                <div className="space-y-4">
                  <div className="space-y-3">
                    {userAchievements.achievements.slice(0, 3).map((achievement, idx) => (
                      <div key={idx} className="flex items-center p-3 border border-gray-100 rounded-lg bg-gray-50 shadow-sm transform transition-transform hover:scale-[1.01]">
                        <img referrerPolicy="no-referrer" src={achievement.icon} alt={achievement.name || achievement.title} className="h-10 w-10 mr-4" />
                        <div>
                          <h4 className="font-bold text-gray-800">{achievement.name || achievement.title}</h4>
                          <p className="text-sm text-gray-600">{achievement.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 text-center">
                    <p className="text-indigo-800 font-semibold mb-2">Want to unlock more? 🚀</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      <a href="/training" className="text-xs font-bold bg-indigo-600 text-white px-3 py-1.5 rounded-full hover:bg-indigo-700">Train Now</a>
                      <a href="/crimes" className="text-xs font-bold bg-purple-600 text-white px-3 py-1.5 rounded-full hover:bg-purple-700">Solve Crimes</a>
                      <a href="/day" className="text-xs font-bold bg-amber-500 text-white px-3 py-1.5 rounded-full hover:bg-amber-600">Word of the Day</a>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100 rounded-xl p-6 text-center shadow-sm">
                  <div className="text-4xl mb-3">🏆</div>
                  <h4 className="text-lg font-bold text-indigo-900 mb-2">No Achievements Yet!</h4>
                  <p className="text-gray-600 text-sm mb-5">
                    Start learning and completing challenges to unlock your first achievements. Here's how you can get started:
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <a href="/training" className="flex flex-col items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all group">
                      <Brain className="w-6 h-6 text-indigo-500 mb-2 group-hover:scale-110 transition-transform" />
                      <span className="text-sm font-semibold text-gray-800">Daily Training</span>
                    </a>
                    <a href="/crimes" className="flex flex-col items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all group">
                      <Gamepad2 className="w-6 h-6 text-purple-500 mb-2 group-hover:scale-110 transition-transform" />
                      <span className="text-sm font-semibold text-gray-800">Solve a Crime</span>
                    </a>
                    <a href="/day" className="flex flex-col items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-amber-300 hover:shadow-md transition-all group">
                      <BookOpen className="w-6 h-6 text-amber-500 mb-2 group-hover:scale-110 transition-transform" />
                      <span className="text-sm font-semibold text-gray-800">Word of the Day</span>
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        
      </div>
      <ShareProfileModal 
        isOpen={isShareModalOpen} 
        onClose={() => setIsShareModalOpen(false)}
        user={{
          uid: currentUser.uid,
          username: username || currentUser.displayName || 'User',
          photoURL: selectedAvatarUrl,
          level: userAchievements.totalPoints > 1000 ? 'Expert' : userAchievements.totalPoints > 500 ? 'Advanced' : userAchievements.totalPoints > 100 ? 'Intermediate' : 'Beginner',
          points: userAchievements.totalPoints || 0,
          streak: userProgress.streak || 0
        }}
      />
    </div>
  );
};

export default ProfilePage;
